import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { ChevronDown, Menu, X } from 'lucide-react';
import { QtplanLogo } from '../../components/QtplanLogo';
import { boardsStore, generateBoardId } from '../../stores/boardsStore';
import { getEmployeesArray } from '../../employees/employeesStore';
import { METHODIK, LOESUNGEN, navText } from '../LandingTopNav';
import { useTranslation } from 'react-i18next';

export function AudienceNavbar({ lang, slug: _slug, basePath: _basePath = 'for' }: { lang: 'de' | 'en'; slug: string; basePath?: string }) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const t = navText[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goToHome = useCallback(() => {
    window.scrollTo(0, 0);
    navigate({ to: '/' });
  }, [navigate]);

  const goToBoards = useCallback(() => {
    window.scrollTo(0, 0);
    navigate({ to: '/boards' });
  }, [navigate]);

  const goToCanvas = useCallback(() => {
    const now = new Date().toISOString();
    const today = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;
    const boardId = generateBoardId('canvas');
    const board = {
      id: boardId,
      name: 'Canvas',
      startDate: today,
      createdAt: now,
      updatedAt: now,
      employeeIds: getEmployeesArray().map(e => e.id),
    };
    boardsStore.send({ type: 'board.create', board });
    window.scrollTo(0, 0);
    navigate({ to: '/boards/$boardId/canvas', params: { boardId } });
  }, [navigate]);

  return (
    <nav className={`landing-topnav ${scrolled ? 'landing-topnav--scrolled' : ''}`}>
      <div className="landing-topnav-left">
        <button className="landing-topnav-brand" onClick={goToHome}>
          <QtplanLogo size={18} className="text-white" />
          <span className="landing-topnav-wordmark">qtplan</span>
        </button>
      </div>

      <div className="landing-topnav-center">
        <button className="landing-topnav-link" onClick={goToHome}>
          {lang === 'de' ? 'Startseite' : 'Home'}
        </button>

        <div className="landing-topnav-dropdown">
          <button className="landing-topnav-link landing-topnav-link--dropdown">
            {t.methodik}
            <ChevronDown size={12} />
          </button>
          <div className="landing-topnav-dropdown-menu">
            <div className="landing-topnav-dropdown-panel">
              {METHODIK.map((item) => (
                <Link key={item.slug} to={`/${lang}/features/${item.slug}`} className="landing-topnav-dropdown-item">
                  <span className="landing-topnav-dropdown-title">{lang === 'de' ? item.de : item.en}</span>
                  <span className="landing-topnav-dropdown-desc">{lang === 'de' ? item.deSub : item.enSub}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="landing-topnav-dropdown">
          <button className="landing-topnav-link landing-topnav-link--dropdown">
            {t.loesungen}
            <ChevronDown size={12} />
          </button>
          <div className="landing-topnav-dropdown-menu">
            <div className="landing-topnav-dropdown-panel">
              {LOESUNGEN.map((item) => (
                <Link key={item.slug} to={`/${lang}/for/${item.slug}`} className="landing-topnav-dropdown-item">
                  <span className="landing-topnav-dropdown-title">{lang === 'de' ? item.de : item.en}</span>
                  <span className="landing-topnav-dropdown-desc">{lang === 'de' ? item.deSub : item.enSub}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <Link className="landing-topnav-link" to={`/${lang}/pricing`}>
          No Pricing
        </Link>

        <Link className="landing-topnav-link" to={`/${lang}/legal/impressum`}>
          {lang === 'de' ? 'Impressum & AGB' : 'Legal & Terms'}
        </Link>
      </div>

      <div className="landing-topnav-right">
        <button className="landing-topnav-cta" onClick={goToCanvas}>
          {t.createCanvas}
        </button>
        <button className="landing-topnav-cta" onClick={goToBoards}>
          {t.createBoard}
        </button>
        <button
          className="landing-topnav-link"
          onClick={() => i18n.changeLanguage(lang === 'de' ? 'en' : 'de')}
        >
          {lang === 'de' ? 'EN' : 'DE'}
        </button>
      </div>

      {/* Mobile hamburger */}
      <button
        className="landing-topnav-mobile-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile menu drawer */}
      {mobileMenuOpen && (
        <div className="landing-topnav-mobile-menu">
          <button className="landing-topnav-mobile-link" onClick={() => { goToHome(); setMobileMenuOpen(false); }}>
            {lang === 'de' ? 'Startseite' : 'Home'}
          </button>

          <div className="landing-topnav-mobile-section">{t.methodik}</div>
          {METHODIK.map((item) => (
            <Link key={item.slug} to={`/${lang}/features/${item.slug}`} className="landing-topnav-mobile-link landing-topnav-mobile-link--sub" onClick={() => setMobileMenuOpen(false)}>
              {lang === 'de' ? item.de : item.en}
            </Link>
          ))}

          <div className="landing-topnav-mobile-section">{t.loesungen}</div>
          {LOESUNGEN.map((item) => (
            <Link key={item.slug} to={`/${lang}/for/${item.slug}`} className="landing-topnav-mobile-link landing-topnav-mobile-link--sub" onClick={() => setMobileMenuOpen(false)}>
              {lang === 'de' ? item.de : item.en}
            </Link>
          ))}

          <Link className="landing-topnav-mobile-link" to={`/${lang}/pricing`} onClick={() => setMobileMenuOpen(false)}>
            No Pricing
          </Link>

          <Link className="landing-topnav-mobile-link" to={`/${lang}/legal/impressum`} onClick={() => setMobileMenuOpen(false)}>
            {lang === 'de' ? 'Impressum & AGB' : 'Legal & Terms'}
          </Link>

          <div style={{ borderTop: '1px solid hsla(0,0%,100%,0.08)', margin: '8px 0' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 16px' }}>
            <button
              onClick={() => i18n.changeLanguage('de')}
              className={`landing-topnav-lang-option ${lang === 'de' ? 'landing-topnav-lang-option--active' : 'landing-topnav-lang-option--inactive'}`}
            >
              DE
            </button>
            <button
              onClick={() => i18n.changeLanguage('en')}
              className={`landing-topnav-lang-option ${lang === 'en' ? 'landing-topnav-lang-option--active' : 'landing-topnav-lang-option--inactive'}`}
            >
              EN
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
